import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { decodeJwt, SignJWT, importPKCS8 } from 'jose';
import { firstValueFrom } from 'rxjs';
import { Environment } from '@thiememeulenhoff/types';
import { TokenInterface } from '../interface';

export interface JwtPlatformConfig {
  serviceAccountEmail: string;
  privateKeyPem: string;
  environment: Environment;
}

export const JwtPlatformConfigToken = new InjectionToken<JwtPlatformConfig>(
  'JwtPlatformConfig',
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DEFAULT_ENDPOINTS: Record<Environment, string> = {
  [Environment.ThipDevelopment]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Development]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Test]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Acceptance]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Staging]:
    'https://platforms.thiememeulenhoff.nl/api/v1/auth/token',
  [Environment.Production]:
    'https://platforms.thiememeulenhoff.nl/api/v1/auth/token',
};

const DEFAULT_AUTH_ENDPOINTS: Record<Environment, string> = {
  [Environment.ThipDevelopment]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth/',
  [Environment.Development]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth',
  [Environment.Test]: 'https://test.platforms.thiememeulenhoff.nl/api/v1/auth',
  [Environment.Acceptance]:
    'https://test.platforms.thiememeulenhoff.nl/api/v1/auth',
  [Environment.Staging]: 'https://platforms.thiememeulenhoff.nl/api/v1/auth',
  [Environment.Production]: 'https://platforms.thiememeulenhoff.nl/api/v1/auth',
};

@Injectable({
  providedIn: 'root',
})
export class JwtPlatformService {
  readonly #http = inject(HttpClient);

  readonly #storage = inject(OAuthStorage);

  readonly #config = inject(JwtPlatformConfigToken);

  readonly #storageKey = 'print_platform_access_token';

  #accessToken: string | null = null;

  get isLoggedIn(): boolean {
    if (!this.#accessToken) {
      return false;
    }

    try {
      const decodedJwt = decodeJwt(this.#accessToken);

      return decodedJwt.exp ? decodedJwt.exp > Date.now() / 1000 : false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to decode access token', error);
      return false;
    }
  }

  initialize(): void {
    const storedToken = this.#storage.getItem(this.#storageKey);

    if (!storedToken) {
      return;
    }

    this.#accessToken = storedToken;

    if (!this.isLoggedIn) {
      this.clear();
    }
  }

  async getSignedJwt(): Promise<string> {
    const privateKey = await importPKCS8(this.#config.privateKeyPem, 'RS256');

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const expiresAtInSeconds = nowInSeconds + 60;

    const jwtBuilder = new SignJWT()
      .setProtectedHeader({
        alg: 'RS256',
        typ: 'JWT',
      })
      .setIssuedAt(nowInSeconds)
      .setExpirationTime(expiresAtInSeconds)
      .setIssuer(
        `urn:thiememeulenhoff:auth:serviceaccount_email:${this.#config.serviceAccountEmail}`,
      )
      .setAudience(DEFAULT_AUTH_ENDPOINTS[this.#config.environment]);

    return jwtBuilder.sign(privateKey);
  }

  async getAccessToken(): Promise<string> {
    if (this.isLoggedIn && this.#accessToken) {
      return this.#accessToken;
    }

    return this.refresh();
  }

  async refresh(): Promise<string> {
    const signedJwt = await this.getSignedJwt();

    const payload = `grant_type=urn:ietf:params:oauth:grant-type:jwt-bearer&assertion=${signedJwt}`;

    const response = await firstValueFrom(
      this.#http.post<TokenInterface>(
        DEFAULT_ENDPOINTS[this.#config.environment],
        payload,
      ),
    );

    this.#storeToken(response.access_token);

    return response.access_token;
  }

  clear(): void {
    this.#accessToken = null;
    this.#storage.removeItem(this.#storageKey);
  }

  #storeToken(token: string): void {
    this.#accessToken = token;
    this.#storage.setItem(this.#storageKey, token);
  }
}
